/**
 * The twenty avatars, drawn in code.
 *
 * No files: a sprite sheet would be another request on a connection that is
 * already loading a WebGL scene, and twenty flat icons are a few kilobytes of
 * markup that gzip to almost nothing. The same reasoning as the boxer and the
 * bag on the canvas, applied to the DOM instead.
 *
 * They are SVG rather than PixiJS Graphics because all three places they appear
 * -- the drawer, the bet list, the drawer's own header -- are DOM. Drawing them
 * with the renderer would mean rasterising twenty textures to put them in an
 * <li>.
 *
 * Each one is a rounded background plus two or three shapes. Nothing here is
 * cropped to a circle in code: the grid and the feed decide the frame, so the
 * same glyph works at 20px in a list row and at 56px in a chooser.
 *
 * The id is what is stored (users.avatar_id, 0..19) and it is a promise: an
 * avatar's number may never come to mean a different picture, or every player
 * who chose one silently becomes someone else. Adding a twenty-first appends.
 */

import { AVATAR_COUNT } from '../net/protocol';

export { AVATAR_COUNT };

/** A drawing: the background colour, and the shapes on top of it. */
interface AvatarSpec {
  bg: string;
  ink: string;
  /** Inner SVG markup, drawn in a 40x40 box. */
  body: string;
}

const R = 'rx="12"';

const SPECS: AvatarSpec[] = [
  // 0 — lion. Also the fallback for an id we do not know, so it is the one that
  // has to read at 20px in a list row as well as at 56px in the chooser: a mane
  // of spikes around a lighter face, rather than a circle with eyes.
  {
    bg: '#3a2a12',
    ink: '#f0b429',
    body: `<g fill="#7a4a14">
        <path d="M20 4l3 5-6 0z"/><path d="M20 36l3-5-6 0z"/>
        <path d="M4 20l5-3 0 6z"/><path d="M36 20l-5-3 0 6z"/>
        <path d="M8 8l5.5 1.5-4 4z"/><path d="M32 8l-5.5 1.5 4 4z"/>
        <path d="M8 32l5.5-1.5-4-4z"/><path d="M32 32l-5.5-1.5 4-4z"/>
      </g>
      <circle cx="20" cy="20" r="12" fill="#8a5a1c"/>
      <circle cx="13.5" cy="13.5" r="3.2" fill="#6b3d10"/><circle cx="26.5" cy="13.5" r="3.2" fill="#6b3d10"/>
      <circle cx="20" cy="21" r="8.5" fill="#f0b429"/>
      <circle cx="16.6" cy="19.5" r="1.4" fill="#2a1c06"/><circle cx="23.4" cy="19.5" r="1.4" fill="#2a1c06"/>
      <path d="M20 22.5l-2 1.5h4z" fill="#2a1c06"/>
      <path d="M17 26.5q3 2 6 0" stroke="#2a1c06" stroke-width="1.4" fill="none" stroke-linecap="round"/>`,
  },
  // 1 — cat
  {
    bg: '#1d2432',
    ink: '#cfd8e6',
    body: `<path d="M11 16l1-7 6 4h4l6-4 1 7z" fill="#8e9aab"/>
      <circle cx="20" cy="22" r="9" fill="#cfd8e6"/>
      <path d="M16.5 21.5l1.6 1.6M23.5 21.5l-1.6 1.6" stroke="#1d2432" stroke-width="1.6" stroke-linecap="round"/>
      <path d="M18 26q2 1.6 4 0" stroke="#1d2432" stroke-width="1.4" fill="none" stroke-linecap="round"/>`,
  },
  // 2 — wolf
  {
    bg: '#16202c',
    ink: '#9fb2c9',
    body: `<path d="M10 14l3-6 5 5h4l5-5 3 6-1 8-9 6-9-6z" fill="#9fb2c9"/>
      <circle cx="16.5" cy="19" r="1.4" fill="#101820"/><circle cx="23.5" cy="19" r="1.4" fill="#101820"/>
      <path d="M20 23l-2 3h4z" fill="#101820"/>`,
  },
  // 3 — bear
  {
    bg: '#2a1f18',
    ink: '#a9754a',
    body: `<circle cx="12" cy="13" r="4.5" fill="#8a5f3c"/><circle cx="28" cy="13" r="4.5" fill="#8a5f3c"/>
      <circle cx="20" cy="21" r="11" fill="#a9754a"/>
      <ellipse cx="20" cy="25" rx="5" ry="4" fill="#e0c3a5"/>
      <circle cx="16" cy="18.5" r="1.4" fill="#2a1710"/><circle cx="24" cy="18.5" r="1.4" fill="#2a1710"/>
      <circle cx="20" cy="23.5" r="1.7" fill="#2a1710"/>`,
  },
  // 4 — bull
  {
    bg: '#301a1c',
    ink: '#e05a5a',
    body: `<path d="M6 12q6-3 8 3M34 12q-6-3-8 3" stroke="#e6dccd" stroke-width="3" fill="none" stroke-linecap="round"/>
      <path d="M12 15h16v9a8 8 0 0 1-16 0z" fill="#8f3b3b"/>
      <circle cx="16.5" cy="20" r="1.4" fill="#f3e6d8"/><circle cx="23.5" cy="20" r="1.4" fill="#f3e6d8"/>
      <ellipse cx="20" cy="26" rx="4" ry="3" fill="#e05a5a"/>`,
  },
  // 5 — fox
  {
    bg: '#31200f',
    ink: '#ef8b3c',
    body: `<path d="M9 12l4 6-2 4z" fill="#c96a24"/><path d="M31 12l-4 6 2 4z" fill="#c96a24"/>
      <path d="M20 10l10 8-10 14-10-14z" fill="#ef8b3c"/>
      <path d="M20 24l10-6-10 14-10-14z" fill="#f7f2ea" opacity=".85"/>
      <circle cx="16.5" cy="20" r="1.3" fill="#26170a"/><circle cx="23.5" cy="20" r="1.3" fill="#26170a"/>`,
  },
  // 6 — owl
  {
    bg: '#1a2130',
    ink: '#c9a227',
    body: `<path d="M10 16q0-9 10-9t10 9v8q0 8-10 8T10 24z" fill="#6b5a44"/>
      <circle cx="15.5" cy="19" r="4.6" fill="#f3ead9"/><circle cx="24.5" cy="19" r="4.6" fill="#f3ead9"/>
      <circle cx="15.5" cy="19" r="2" fill="#1b1408"/><circle cx="24.5" cy="19" r="2" fill="#1b1408"/>
      <path d="M20 22l-2.5 4h5z" fill="#c9a227"/>`,
  },
  // 7 — eagle. A head in profile: the hooked beak is what makes it a bird of prey
  // rather than a white oval, so it gets the strongest shape and the only accent
  // colour.
  {
    bg: '#141c26',
    ink: '#e8eef7',
    body: `<path d="M13 30q-4-6-2-13Q13 8 22 8q7 0 9 6-6-1-9 2-4 4-3 10z" fill="#f3f6fb"/>
      <path d="M22 8q7 0 9 6-6-1-9 2z" fill="#c3cfdd"/>
      <path d="M13 30q6 3 12 1l-3 4z" fill="#c3cfdd"/>
      <circle cx="19" cy="15.5" r="2.1" fill="#111820"/>
      <circle cx="19.7" cy="14.9" r="0.7" fill="#f3f6fb"/>
      <path d="M14.5 18q-6 1-8 4 3 1 6 0.5 2-.4 3-1.5z" fill="#f0b429"/>
      <path d="M6.5 22q2 1.5 4.5 1.5-2 1.5-4 1z" fill="#c9911d"/>`,
  },
  // 8 — shark
  {
    bg: '#0f2230',
    ink: '#7fb4d6',
    body: `<path d="M4 26q10-16 32-14-4 10-16 12z" fill="#5d8fb3"/>
      <path d="M20 12l4-7 3 8z" fill="#7fb4d6"/>
      <path d="M8 24q8 3 18 0" stroke="#e8f3fa" stroke-width="1.6" fill="none"/>
      <circle cx="25" cy="16" r="1.4" fill="#0c1a24"/>`,
  },
  // 9 — snake
  {
    bg: '#12291d',
    ink: '#5ad07f',
    body: `<path d="M10 30q0-6 6-6t6-4-4-5 2-7 8 2" stroke="#3f9c5e" stroke-width="5" fill="none" stroke-linecap="round"/>
      <circle cx="28" cy="10" r="4.5" fill="#5ad07f"/>
      <circle cx="29.5" cy="9" r="1.2" fill="#0d1f14"/>
      <path d="M32 12l4 2-4 1z" fill="#e05a5a"/>`,
  },
  // 10 — frog
  {
    bg: '#15291a',
    ink: '#6fd05a',
    body: `<ellipse cx="20" cy="24" rx="13" ry="9" fill="#4f9c3f"/>
      <circle cx="13" cy="15" r="5" fill="#6fd05a"/><circle cx="27" cy="15" r="5" fill="#6fd05a"/>
      <circle cx="13" cy="15" r="2" fill="#10200f"/><circle cx="27" cy="15" r="2" fill="#10200f"/>
      <path d="M13 26q7 4 14 0" stroke="#10200f" stroke-width="1.6" fill="none" stroke-linecap="round"/>`,
  },
  // 11 — panda
  {
    bg: '#1c2028',
    ink: '#f3f6fb',
    body: `<circle cx="12" cy="13" r="4.5" fill="#20242c"/><circle cx="28" cy="13" r="4.5" fill="#20242c"/>
      <circle cx="20" cy="21" r="11" fill="#f3f6fb"/>
      <ellipse cx="15.5" cy="19" rx="3.2" ry="4" fill="#20242c" transform="rotate(-12 15.5 19)"/>
      <ellipse cx="24.5" cy="19" rx="3.2" ry="4" fill="#20242c" transform="rotate(12 24.5 19)"/>
      <circle cx="20" cy="25" r="2" fill="#20242c"/>`,
  },
  // 12 — trophy
  {
    bg: '#2b2410',
    ink: '#f0b429',
    body: `<path d="M12 8h16v6a8 8 0 0 1-16 0z" fill="#f0b429"/>
      <path d="M12 10H8v3a5 5 0 0 0 5 5M28 10h4v3a5 5 0 0 1-5 5" stroke="#c9911d" stroke-width="2" fill="none"/>
      <rect x="18" y="21" width="4" height="6" fill="#c9911d"/>
      <rect x="13" y="27" width="14" height="4" rx="1.5" fill="#f0b429"/>`,
  },
  // 13 — star
  {
    bg: '#221c33',
    ink: '#c4b5fd',
    body: `<path d="M20 7l4.2 8.6 9.5 1.4-6.9 6.7 1.7 9.4L20 28.6l-8.5 4.5 1.7-9.4-6.9-6.7 9.5-1.4z" fill="#c4b5fd"/>
      <path d="M20 12.5l2.4 5 5.5.8-4 3.9 1 5.5-4.9-2.6z" fill="#efeaff"/>`,
  },
  // 14 — boxing glove
  {
    bg: '#2c1417',
    ink: '#ff4d4f',
    body: `<path d="M11 15q0-7 8-7t8 7v6q0 7-8 7t-8-7z" fill="#d43f42"/>
      <path d="M27 14q4 1 4 5t-4 5z" fill="#d43f42"/>
      <rect x="11" y="27" width="16" height="5" rx="2" fill="#8f2a2c"/>
      <path d="M13 20q6 3 12 0" stroke="#ff8b8d" stroke-width="1.6" fill="none"/>`,
  },
  // 15 — medal
  {
    bg: '#122430',
    ink: '#8ecae6',
    body: `<path d="M13 6l5 10M27 6l-5 10" stroke="#5c7f96" stroke-width="3.4" stroke-linecap="round"/>
      <circle cx="20" cy="24" r="9" fill="#8ecae6"/>
      <circle cx="20" cy="24" r="5.5" fill="#e8f4fb"/>
      <path d="M20 20.5l1.4 2.8 3.1.5-2.3 2.2.6 3.1-2.8-1.5-2.8 1.5.6-3.1-2.3-2.2 3.1-.5z" fill="#5c7f96"/>`,
  },
  // 16 — dice
  {
    bg: '#101a24',
    ink: '#e8eef7',
    body: `<rect x="8" y="8" width="24" height="24" rx="6" fill="#e8eef7"/>
      <circle cx="15" cy="15" r="2.2" fill="#101a24"/><circle cx="25" cy="15" r="2.2" fill="#101a24"/>
      <circle cx="20" cy="20" r="2.2" fill="#101a24"/>
      <circle cx="15" cy="25" r="2.2" fill="#101a24"/><circle cx="25" cy="25" r="2.2" fill="#101a24"/>`,
  },
  // 17 — bolt
  {
    bg: '#1d2410',
    ink: '#d8f42a',
    body: `<path d="M23 5L11 22h7l-3 13 14-18h-8l4-12z" fill="#d8f42a"/>`,
  },
  // 18 — crown
  {
    bg: '#2a2110',
    ink: '#ffd166',
    body: `<path d="M7 28l2-16 6 6 5-9 5 9 6-6 2 16z" fill="#ffd166"/>
      <rect x="7" y="28" width="26" height="4" rx="1.5" fill="#d9a83c"/>
      <circle cx="20" cy="17" r="1.8" fill="#8f3b3b"/>`,
  },
  // 19 — rocket
  {
    bg: '#151d2c',
    ink: '#9ec5ff',
    body: `<path d="M20 5q7 7 7 16l-7 4-7-4q0-9 7-16z" fill="#e8eef7"/>
      <circle cx="20" cy="15" r="3" fill="#3b82f6"/>
      <path d="M13 20l-4 6 6-2M27 20l4 6-6-2" fill="#9ec5ff"/>
      <path d="M17 27h6l-3 7z" fill="#f0b429"/>`,
  },
];

if (SPECS.length !== AVATAR_COUNT) {
  // A mismatch here would let a stored id point at nothing, so it is a
  // programming error worth failing loudly on rather than a fallback.
  throw new Error(`avatars: ${SPECS.length} drawings for ${AVATAR_COUNT} ids`);
}

/** Clamps anything off the wire into a drawable id. */
export function normaliseAvatarId(id: number | undefined | null): number {
  if (typeof id !== 'number' || !Number.isFinite(id)) return 0;
  const rounded = Math.trunc(id);
  return rounded >= 0 && rounded < AVATAR_COUNT ? rounded : 0;
}

/**
 * Builds one avatar as an SVG element.
 *
 * `title` is left off deliberately: these appear beside a masked nickname in a
 * list of dozens, and a tooltip on each one is noise. The chooser adds its own
 * label, which is where a name is actually useful.
 */
export function avatarSvg(id: number): SVGSVGElement {
  const spec = SPECS[normaliseAvatarId(id)]!;
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 40 40');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  // innerHTML on an SVG element is set from a constant in this module; no value
  // from the server or from a player ever reaches it.
  svg.innerHTML = `<rect width="40" height="40" ${R} fill="${spec.bg}"/>${spec.body}`;
  return svg;
}

/** The ink colour of an avatar, for a caller that wants to match a border. */
export function avatarInk(id: number): string {
  return SPECS[normaliseAvatarId(id)]!.ink;
}
